import React, { useMemo, useState, useEffect } from "react";
import "../styles/App.css";
import { ensureAppSettings, saveAppSettings, cascadeDeleteRelationType } from "../services/data";

// ----------------------
// Utils
// ----------------------
const slugify = (s = "") =>
  (s || "")
    .normalize?.("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const uid = () => Math.random().toString(36).slice(2, 10);
// Enregistre: "user" -> "fas fa-user"
const normIcon = (ic) =>
  /^(fas|far|fal|fab)\sfa-/.test(ic || "") ? ic : `fas fa-${ic || "users"}`;

// Charge: "fas fa-user" -> "user" (pour l'UI)
const denormIcon = (ic) =>
  (ic || "").replace(/^(fas|far|fal|fab)\sfa-/, "") || "user";

// ID stable = key
const stableIdFromType = (t) =>
  t?.id ? t.id : t?.key || slugify(t?.name || t?.label || "");

// ----------------------
// Données d'exemple
// ----------------------
const DEFAULT_TYPES = [
  {
    id: uid(),
    name: "Client",
    key: "client",
    icon: "user-tie",
    color: "#3b82f6",
    order: 1,
  },
  {
    id: uid(),
    name: "Fournisseur",
    key: "fournisseur",
    icon: "truck",
    color: "#f59e0b",
    order: 2,
  },
];

const DEFAULT_FIELDS = [
  {
    id: uid(),
    name: "Site web",
    key: "site_web",
    type: "text",
    options: "",
    applicableIds: [],
    required: false,
    order: 1,
  },
  {
    id: uid(),
    name: "SIRET",
    key: "siret",
    type: "text",
    options: "",
    applicableIds: [],
    required: false,
    order: 2,
  },
  {
    id: uid(),
    name: "Secteur d'activité",
    key: "secteur",
    type: "select",
    options: "Technologie;Santé;Finance",
    applicableIds: [],
    required: false,
    order: 3,
  },
];

const ICONS = [
  "user",
  "user-tie",
  "users",
  "handshake",
  "briefcase",
  "building",
  "store",
  "truck",
  "industry",
  "id-badge",
  "address-card",
  "phone",
  "envelope",
  "globe",
  "link",
];

const FIELD_TYPES = [
  { value: "text", label: "Texte" },
  { value: "number", label: "Nombre" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Téléphone" },
  { value: "date", label: "Date" },
  { value: "select", label: "Liste déroulante" },
  { value: "checkbox", label: "Case à cocher" },
];

export default function EditRelations({ user, onBack, onSaved }) {
  // ----------------------
  // État principal
  // ----------------------
  const [relationName, setRelationName] = useState("Relations");
  const [relationDescription, setRelationDescription] = useState(
    "Gérez vos contacts professionnels"
  );
  const [relationTypes, setRelationTypes] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  useEffect(() => {
    (async () => {
      if (!user?.uid) return;
      const app = await ensureAppSettings(user.uid);
      const data = app?.relations || null;
      if (!data) return;

      const loadedTypes = (data.types || []).map((t) => ({
        id: stableIdFromType(t), // [ok] id toujours présent et stable
        name: t.name || t.label || t.key || "",
        key: t.key || slugify(t.name || t.label || ""),
        icon: denormIcon(t.icon), // "fas fa-user" -> "user"
        color: t.color || "#3b82f6",
        order: t.order ?? 0,
        visible: t.visible !== false,
      }));

      // Optionnel: migration d'anciens champs qui contiennent des keys au lieu d'ids
      const loadedFields = (data.fields || []).map((f) => ({
        id: f.id || f.key || uid(), // [ok] id garanti
        name: f.name || f.label || f.key || "",
        key: f.key,
        type: f.type || "text",
        options: f.options || "",
        applicableIds: Array.isArray(f.applicableIds) ? f.applicableIds : [],
        required: !!f.required,
        order: f.order ?? 0,
      }));
      setRelationName(data.main?.label || "Relations");
      setRelationDescription(data.desc || "");
      setRelationTypes(loadedTypes);
      setCustomFields(loadedFields);
      if (
        (loadedTypes?.length || 0) === 0 &&
        (loadedFields?.length || 0) === 0
      ) {
        const seeded = [
          {
            id: "client",
            name: "Client",
            key: "client",
            icon: "user-tie",
            color: "#3b82f6",
            order: 1,
            visible: true,
          },
        ];
        setRelationTypes(seeded);
        setCustomFields([]);
        onSaveAll(seeded, []); // pousse le seed dans app_settings
      }

      // [wrench] Sauvegarde de normalisation (rajoute id + name + icônes normalisées)
      // (évite de reperdre id/name aux prochains refresh)
      onSaveAll(loadedTypes, loadedFields);
    })();
  }, [user?.uid]);

  // Persist
  //useEffect(() => saveLocal("rel_name", relationName), [relationName]);
  //useEffect(() => saveLocal("rel_desc", relationDescription), [relationDescription]);
  //useEffect(() => saveLocal("rel_types", relationTypes), [relationTypes]);
  //useEffect(() => saveLocal("rel_fields", customFields), [customFields]);

  // ----------------------
  // Modales + drafts
  // ----------------------
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [typeDraft, setTypeDraft] = useState({
    name: "",
    key: "",
    icon: "user",
    color: "#3b82f6",
    order: 1,
  });
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [fieldDraft, setFieldDraft] = useState({
    name: "",
    key: "",
    type: "text",
    options: "",
    applicableIds: [],
    required: false,
    order: 1,
  });
  // Filtres pour "Champs personnalisés"
  const [qFields, setQFields] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // 'all' | 'none' | <typeId> | <typeKey> (toléré)

  // Verrouille le scroll quand une modale est ouverte
  useEffect(() => {
    const open = showTypeModal || showFieldModal;
    const original = document.body.style.overflow;
    document.body.style.overflow = open ? "hidden" : original || "";
    return () => {
      document.body.style.overflow = original || "";
    };
  }, [showTypeModal, showFieldModal]);

  // Fermer modales si clic en dehors
  const onOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      if (showTypeModal) setShowTypeModal(false);
      if (showFieldModal) setShowFieldModal(false);
    }
  };

  const sortedTypes = useMemo(
    () =>
      [...relationTypes].sort(
        (a, b) =>
          (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name)
      ),
    [relationTypes]
  );
  const sortedFields = useMemo(
    () =>
      [...customFields].sort(
        (a, b) =>
          (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name)
      ),
    [customFields]
  );
  // Maps pour tolérer anciens enregistrements (id ou key)
  const idByKey = useMemo(
    () => Object.fromEntries(relationTypes.map((t) => [t.key, t.id])),
    [relationTypes]
  );
  const keyById = useMemo(
    () => Object.fromEntries(relationTypes.map((t) => [t.id, t.key])),
    [relationTypes]
  );
  const filteredFields = useMemo(() => {
    let arr = sortedFields;

    // --- Filtre par type ---
    if (typeFilter === "none") {
      // Champs non assignés à un type
      arr = arr.filter((f) => !f.applicableIds || f.applicableIds.length === 0);
    } else if (typeFilter !== "all") {
      // On accepte que typeFilter soit un id OU un key
      const wantedId = idByKey[typeFilter] ?? typeFilter;
      const wantedKey = keyById[typeFilter] ?? typeFilter;

      arr = arr.filter((f) => {
        const ids = f.applicableIds || [];
        // champ applicable si la liste contient l'id OU (par tolérance) la key
        return ids.includes(wantedId) || ids.includes(wantedKey);
      });
    }

    // --- Recherche plein-texte ---
    const q = qFields.trim().toLowerCase();
    if (q) {
      arr = arr.filter((f) => {
        const applicableNames = (f.applicableIds || [])
          .map((id) => relationTypes.find((t) => t.id === id)?.name || "")
          .join(" ");
        return [f.name, f.key, f.type, f.options, applicableNames].some((v) =>
          (v ?? "").toString().toLowerCase().includes(q)
        );
      });
    }

    // Le tri est déjà fait dans sortedFields -> on renvoie tel quel
    return arr;
  }, [sortedFields, typeFilter, qFields, relationTypes, idByKey, keyById]);

  // ----------------------
  // Types - CRUD
  // ----------------------
  const openAddType = () => {
    setEditingTypeId(null);
    setTypeDraft({
      name: "",
      key: "",
      icon: "user",
      color: "#3b82f6",
      order: (relationTypes.length || 0) + 1,
    });
    setShowTypeModal(true);
  };

  const openEditType = (t) => {
    setEditingTypeId(t.id);
    setTypeDraft({
      name: t.name,
      key: t.key,
      icon: t.icon,
      color: t.color,
      order: t.order ?? 1,
    });
    setShowTypeModal(true);
  };

  const removeType = (id) => {
    const removedKey = keyById[id] || id;
    setRelationTypes((prevTypes) => {
      const nextTypes = prevTypes.filter((t) => t.id !== id);
      setCustomFields((prevFields) => {
        const nextFields = prevFields.map((f) => ({
          ...f,
          applicableIds: (f.applicableIds || []).filter(
            (x) => x !== id && x !== removedKey
          ),
        }));
        onSaveAll(nextTypes, nextFields);
        return nextFields;
      });
      return nextTypes;
    });
    if (user?.uid && removedKey) {
      cascadeDeleteRelationType(user.uid, removedKey).catch((err) => {
        console.error(
          `[EditRelations] cascade delete failed for ${removedKey}:`,
          err?.message || err
        );
      });
    }
  };

  const saveType = () => {
    const { name, key, icon, color, order } = typeDraft;
    if (!name.trim()) return alert("Le nom est requis.");
    if (!key.trim()) return alert("La key est requise.");

    const nextId = key; // [ok] id stable = key

    if (editingTypeId) {
      // Édition : si la key change, on migre les applicableIds des champs
      setRelationTypes((prevTypes) => {
        const prev = [...prevTypes];
        const old = prev.find((t) => t.id === editingTypeId);
        const oldId = old?.id ?? editingTypeId;

        const updatedTypes = prev.map((t) =>
          t.id === editingTypeId
            ? {
                ...t,
                id: nextId,
                name,
                key,
                icon,
                color,
                order: Number(order) || 0,
              }
            : t
        );

        if (oldId && oldId !== nextId) {
          setCustomFields((prevFields) => {
            const migrated = prevFields.map((f) => {
              const ids = new Set(f.applicableIds || []);
              if (ids.has(oldId)) {
                ids.delete(oldId);
                ids.add(nextId);
              }
              return { ...f, applicableIds: Array.from(ids) };
            });
            // [refresh] Sauvegarde types + champs migrés
            onSaveAll(updatedTypes, migrated);
            return migrated;
          });
        } else {
          onSaveAll(updatedTypes, customFields);
        }

        return updatedTypes;
      });
    } else {
      // Ajout
      const newType = {
        id: nextId, // [ok] id stable = key
        name,
        key,
        icon,
        color,
        order: Number(order) || 0,
        visible: true,
      };
      setRelationTypes((prev) => {
        const next = [...prev, newType];
        onSaveAll(next, customFields);
        return next;
      });
    }

    setShowTypeModal(false);
    setEditingTypeId(null);
  };

  // ----------------------
  // Champs - CRUD
  // ----------------------
  const openAddField = () => {
    setEditingFieldId(null);
    setFieldDraft({
      name: "",
      key: "",
      type: "text",
      options: "",
      applicableIds: [],
      required: false,
      order: (customFields.length || 0) + 1,
    });
    setShowFieldModal(true);
  };

  const openEditField = (f) => {
    setEditingFieldId(f.id);
    setFieldDraft({
      name: f.name,
      key: f.key,
      type: f.type,
      options: f.options || "",
      applicableIds: f.applicableIds || [],
      required: !!f.required,
      order: f.order ?? 1,
    });
    setShowFieldModal(true);
  };

  const removeField = (id) => {
    setCustomFields((prev) => {
      const next = prev.filter((f) => f.id !== id);
      // [ok] Persistance immédiate
      onSaveAll(relationTypes, next);
      return next;
    });
  };

  const saveField = () => {
    const { name, key, type, options, applicableIds, required, order } =
      fieldDraft;
    if (!name.trim()) return alert("Le nom du champ est requis.");
    if (!key.trim()) return alert("La key du champ est requise.");
    // Normaliser options: accepter "," ou ";" puis re-joindre avec ","
    const normOptions = (options || "")
      .split(/[;,]/g)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(",");
    const payload = {
      name,
      key,
      type,
      options: type === "select" || type === "checkbox" ? normOptions : "",
      applicableIds: applicableIds || [], // on garde des ids (RelationPage attend des ids)
      required: !!required,
      order: Number(order) || 0,
    };

    if (editingFieldId) {
      setCustomFields((prev) => {
        const next = prev.map((f) =>
          f.id === editingFieldId ? { ...f, ...payload } : f
        );
        // [ok] Sauvegarde après maj d'état
        onSaveAll(relationTypes, next);
        return next;
      });
    } else {
      setCustomFields((prev) => {
        const next = [...prev, { id: uid(), ...payload }];
        // [ok] Sauvegarde après maj d'état
        onSaveAll(relationTypes, next);
        return next;
      });
    }

    setShowFieldModal(false);
    setEditingFieldId(null);
  };

  // ----------------------
  // Save all (sauvegarde dynamique pour la Sidebar)
  // ----------------------
  const onSaveAll = async (
    typesOverride = relationTypes,
    fieldsOverride = customFields
  ) => {
    const settings = {
      main: {
        label: relationName || "Relations",
        icon: "fas fa-address-book",
        defaultOpen: true,
      },
      desc: relationDescription,
      types: (typesOverride || []).map((t) => ({
        id: t.id, // [ok] persister id
        name: t.name, // [ok] persister name (pour rechargement)
        label: t.name, // (pour Sidebar)
        key: t.key,
        icon: normIcon(t.icon), // "user" -> "fas fa-user"
        color: t.color,
        visible: t.visible !== false,
        order: t.order ?? 0,
      })),
      fields: fieldsOverride || [],
      updatedAt: Date.now(),
    };

    await saveAppSettings(user.uid, { relations: settings });
    console.log("[ok] relations_settings saved:", settings);
    // pas d'alert ici pour éviter le spam auto-save
    onSaved?.();
  };

  const IconBadge = ({ icon, color }) => (
    <div
      className="icon-badge"
      style={{
        width: 40,
        height: 40,
        borderRadius: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: color || "#e5e7eb",
        color: "#fff",
      }}
      aria-hidden
    >
      <i className={`fas fa-${icon}`} />
    </div>
  );

  const IconPicker = ({ value, onChange }) => (
    <div
      className="icon-picker"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(8, minmax(0, 1fr))",
        gap: 8,
      }}
    >
      {ICONS.map((ic) => (
        <button
          key={ic}
          type="button"
          onClick={() => onChange(ic)}
          className="icon-cell"
          aria-label={`Choisir l'icône ${ic}`}
          style={{
            border: value === ic ? "2px solid #2563eb" : "1px solid #e5e7eb",
            borderRadius: 8,
            padding: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
            cursor: "pointer",
          }}
        >
          <i className={`fas fa-${ic}`} />
        </button>
      ))}
    </div>
  );

  return (
    <div className="edit-relations">
      {/* En-tête de page */}
      <div className="page-header">
        <h1 className="page-title">Configuration des Relations</h1>
        <div className="page-actions">
          <button className="btn btn-outline" onClick={onBack}>
            <i className="fas fa-arrow-left"></i>
            Retour
          </button>
          <button className="btn btn-primary" onClick={onSaveAll}>
            <i className="fas fa-save"></i>
            Enregistrer
          </button>
        </div>
      </div>

      {/* Section Nom de la relation */}
      <div className="config-section">
        <div className="config-header">
          <h3 className="config-title">Nom de la relation</h3>
        </div>
        <div className="config-body">
          <div className="config-form">
            <div className="form-group">
              <label className="form-label">Nom principal</label>
              <input
                type="text"
                className="form-input"
                value={relationName}
                onChange={(e) => setRelationName(e.target.value)}
                placeholder="Relations"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-input form-textarea"
                value={relationDescription}
                onChange={(e) => setRelationDescription(e.target.value)}
                placeholder="Gérez vos contacts professionnels, clients et fournisseurs"
                rows={3}
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      {/* Section Types de relations */}
      <div className="config-section">
        <div className="config-header">
          <h3 className="config-title">Types de relations</h3>
          <button className="btn btn-primary" onClick={openAddType}>
            <i className="fas fa-plus"></i>
            Ajouter
          </button>
        </div>
        <div className="config-body">
          <div className="list-items">
            {sortedTypes.length === 0 && (
              <div
                style={{
                  color: "#6b7280",
                  textAlign: "center",
                  padding: "20px",
                }}
              >
                Aucun type pour le moment.
              </div>
            )}
            {sortedTypes.map((type) => (
              <div key={type.id} className="list-item">
                <div
                  className="list-item-icon"
                  style={{ backgroundColor: type.color }}
                >
                  <i className={`fas fa-${type.icon}`}></i>
                </div>
                <div className="list-item-content">
                  <div className="list-item-title">{type.name}</div>
                  <div className="list-item-subtitle">
                    Key: {type.key} - Icône: {type.icon} - Ordre: {type.order}
                  </div>
                </div>
                <div className="list-item-actions">
                  <div
                    className="action-btn"
                    title="Modifier"
                    onClick={() => openEditType(type)}
                  >
                    <i className="fas fa-edit"></i>
                  </div>
                  <div
                    className="action-btn delete"
                    title="Supprimer"
                    onClick={() => removeType(type.id)}
                  >
                    <i className="fas fa-trash"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section Champs personnalisés */}
      <div className="config-section">
        <div className="config-header">
          <h3 className="config-title">Champs personnalisés</h3>
          <button className="btn btn-primary" onClick={openAddField}>
            <i className="fas fa-plus"></i>
            Ajouter
          </button>
        </div>

        {/* --- Filtres --- */}
        <div className="filters-grid" style={{ marginTop: 8 }}>
          <div className="filter-group">
            <label className="filter-label">Rechercher un champ</label>
            <input
              className="form-input"
              placeholder="Nom, clé, type, options..."
              value={qFields}
              onChange={(e) => setQFields(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Type de relation</label>
            <select
              className="form-input"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">Tous les types</option>
              <option value="none">Non assignés</option>
              {sortedTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="config-body">
          <div className="list-items">
            {filteredFields.length === 0 && (
              <div
                style={{
                  color: "#6b7280",
                  textAlign: "center",
                  padding: "20px",
                }}
              >
                Aucun champ pour ce filtre.
              </div>
            )}

            {filteredFields.map((field) => {
              const applicableTypes = (field.applicableIds || [])
                .map((id) => {
                  const type = relationTypes.find(
                    (t) => t.id === id || t.key === id
                  );
                  return type ? type.name : "";
                })
                .filter(Boolean);

              return (
                <div key={field.id} className="list-item">
                  <div className="list-item-icon">
                    <i className="fas fa-tag"></i>
                  </div>
                  <div className="list-item-content">
                    <div className="list-item-title">
                      {field.name}
                      {field.required && (
                        <span style={{ color: "var(--danger)" }}> *</span>
                      )}
                    </div>
                    <div className="list-item-subtitle">
                      Type: {field.type} - Ordre: {field.order} - Applicable:{" "}
                      {applicableTypes.length
                        ? applicableTypes.join(", ")
                        : "Tous/aucun"}{" "}
                      - Key: {field.key}
                    </div>
                    {(field.type === "select" || field.type === "checkbox") &&
                      field.options && (
                        <div className="list-item-subtitle">
                          Options: {field.options}
                        </div>
                      )}
                  </div>
                  <div className="list-item-actions">
                    <div
                      className="action-btn"
                      title="Modifier"
                      onClick={() => openEditField(field)}
                    >
                      <i className="fas fa-edit"></i>
                    </div>
                    <div
                      className="action-btn delete"
                      title="Supprimer"
                      onClick={() => removeField(field.id)}
                    >
                      <i className="fas fa-trash"></i>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* MODALE: Type de relation */}
      {/* MODALE: Type de relation */}
      {showTypeModal && (
        <div
          className="formula-helper-overlay show"
          onClick={(e) => {
            if (e.target.classList.contains("formula-helper-overlay"))
              setShowTypeModal(false);
          }}
        >
          <div
            className="formula-helper-modal full-page-form"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="formula-helper-header">
              <h3>
                <i className="fas fa-users"></i>
                {editingTypeId
                  ? "Modifier un type de relation"
                  : "Ajouter un type de relation"}
              </h3>
              <button
                className="formula-close-btn"
                onClick={() => setShowTypeModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="formula-helper-content full-page-content">
              <div className="full-page-form-grid">
                <div className="form-group">
                  <label className="form-label full-page-label">Nom</label>
                  <input
                    type="text"
                    className="form-input"
                    value={typeDraft.name}
                    onChange={(e) =>
                      setTypeDraft({ ...typeDraft, name: e.target.value })
                    }
                    placeholder="Ex: Client"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label full-page-label">Key</label>
                  <input
                    type="text"
                    className="form-input"
                    value={typeDraft.key}
                    onChange={(e) =>
                      setTypeDraft({ ...typeDraft, key: e.target.value })
                    }
                    placeholder="Ex: client"
                  />
                </div>
                <div className="form-group full-width">
                  <label className="form-label full-page-label">Icône</label>
                  <IconPicker
                    value={typeDraft.icon}
                    onChange={(icon) => setTypeDraft({ ...typeDraft, icon })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label full-page-label">Couleur</label>
                  <input
                    type="color"
                    className="form-input"
                    value={typeDraft.color}
                    onChange={(e) =>
                      setTypeDraft({ ...typeDraft, color: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label full-page-label">Ordre</label>
                  <input
                    type="number"
                    className="form-input"
                    value={typeDraft.order}
                    onChange={(e) =>
                      setTypeDraft({
                        ...typeDraft,
                        order: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>

              <div className="modal-footer full-page-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowTypeModal(false)}
                >
                  Annuler
                </button>
                <button className="btn btn-primary" onClick={saveType}>
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODALE: Champ personnalisé */}
      {showFieldModal && (
        <div
          className="formula-helper-overlay show"
          onClick={(e) => {
            if (e.target.classList.contains("formula-helper-overlay"))
              setShowFieldModal(false);
          }}
        >
          <div
            className="formula-helper-modal full-page-form"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="formula-helper-header">
              <h3>
                <i className="fas fa-tag"></i>
                {editingFieldId
                  ? "Modifier un champ personnalisé"
                  : "Ajouter un champ personnalisé"}
              </h3>
              <button
                className="formula-close-btn"
                onClick={() => setShowFieldModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="formula-helper-content full-page-content">
              <div className="full-page-form-grid">
                <div className="form-group">
                  <label className="form-label full-page-label">
                    Nom du champ
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={fieldDraft.name}
                    onChange={(e) =>
                      setFieldDraft({ ...fieldDraft, name: e.target.value })
                    }
                    placeholder="Ex: SIRET"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label full-page-label">Key</label>
                  <input
                    type="text"
                    className="form-input"
                    value={fieldDraft.key}
                    onChange={(e) =>
                      setFieldDraft({ ...fieldDraft, key: e.target.value })
                    }
                    placeholder="Ex: siret"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label full-page-label">
                    Type de champ
                  </label>
                  <select
                    className="form-select"
                    value={fieldDraft.type}
                    onChange={(e) =>
                      setFieldDraft({ ...fieldDraft, type: e.target.value })
                    }
                  >
                    {FIELD_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                {(fieldDraft.type === "select" ||
                  fieldDraft.type === "checkbox") && (
                  <div className="form-group full-width">
                    <label className="form-label full-page-label">
                      Options (séparées par ;)
                    </label>
                    <textarea
                      className="form-input full-page-textarea"
                      value={fieldDraft.options}
                      onChange={(e) =>
                        setFieldDraft({
                          ...fieldDraft,
                          options: e.target.value,
                        })
                      }
                      placeholder="Ex: Technologie;Santé;Finance"
                      rows={4}
                    />
                  </div>
                )}

                <div className="form-group full-width">
                  <label className="form-label full-page-label">
                    Applicabilité (multi-sélection)
                  </label>
                  <div className="applicability-grid full-page-applicability">
                    {sortedTypes.map((t) => {
                      const checked = (fieldDraft.applicableIds || []).some(
                        (x) => x === t.id || x === t.key
                      );
                      return (
                        <label
                          key={t.id}
                          className="applicability-item full-page-applicability-item"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = new Set(
                                fieldDraft.applicableIds || []
                              );
                              if (e.target.checked) next.add(t.id);
                              else next.delete(t.id);
                              setFieldDraft({
                                ...fieldDraft,
                                applicableIds: Array.from(next),
                              });
                            }}
                          />
                          <IconBadge icon={t.icon} color={t.color} />
                          <span>{t.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label full-page-checkbox">
                    <input
                      type="checkbox"
                      checked={fieldDraft.required}
                      onChange={(e) =>
                        setFieldDraft({
                          ...fieldDraft,
                          required: e.target.checked,
                        })
                      }
                    />
                    <span>Champ obligatoire</span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="form-label full-page-label">Ordre</label>
                  <input
                    type="number"
                    className="form-input"
                    value={fieldDraft.order}
                    onChange={(e) =>
                      setFieldDraft({
                        ...fieldDraft,
                        order: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>

              <div className="modal-footer full-page-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowFieldModal(false)}
                >
                  Annuler
                </button>
                <button className="btn btn-primary" onClick={saveField}>
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
